/**
 * Analytics Service
 *
 * Handles tracking page views and fetching analytics statistics.
 * In Docker: nginx proxies /api/* to the ai-service container.
 */

import { buildAuthHeaders } from './authHeaderService';

const AI_SERVICE_URL = process.env.REACT_APP_AI_LAMBDA_URL || '/api';

/**
 * Track a page view
 * @param {Object} data - Tracking data
 * @param {string} data.path - The page path
 * @param {string} data.sessionId - Unique session identifier
 * @param {string|null} data.userId - User ID if logged in
 * @param {string|null} data.referrer - Referrer URL
 * @returns {Promise<{success: boolean}>}
 */
export async function trackPageView({ path, sessionId, userId, referrer }) {
    if (!AI_SERVICE_URL) {
        return { success: false };
    }

    try {
        const response = await fetch(`${AI_SERVICE_URL}/analytics/track`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                path,
                sessionId,
                userId,
                referrer
            })
        });

        if (!response.ok) {
            throw new Error(`Request failed: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        // Silently fail - analytics should not break the app
        console.debug('Analytics track error:', error.message);
        return { success: false };
    }
}

/**
 * Get analytics statistics for admin dashboard
 * @param {string} start - Start date (YYYY-MM-DD)
 * @param {string} end - End date (YYYY-MM-DD)
 * @returns {Promise<Object>} Analytics data
 */
export async function getAnalyticsStats(start, end) {
    if (!AI_SERVICE_URL) {
        throw new Error('Analytics service not configured');
    }

    const response = await fetch(
        `${AI_SERVICE_URL}/analytics/stats?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
        {
            method: 'GET',
            headers: await buildAuthHeaders({
                'Content-Type': 'application/json'
            })
        }
    );

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Request failed: ${response.status}`);
    }

    return await response.json();
}
